import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, Lock, Star, Play, Pause, ArrowLeft, ArrowRight, ArrowUp, Trophy } from 'lucide-react';
import { useLab, studentBase, getCourseTemplateForChild, resolveLabChildId } from '../LabContext';
import { LabLayout, Card, ParentPreviewBadge } from '../LabUI';
import { computeDayStatus, getWeeksForCourse } from '../data/lessons';

// ============================================================================
// Nimny Night Runner v8 — canvas runner with per-level backgrounds and run-cycle sprites.
// Pure React + Canvas + image assets. No backend, no analytics, no external SDKs.
// Child lock remains Week 10 Day 5; parent/super_admin keeps preview access.
// ============================================================================

const STORAGE_KEY_BASE = 'nimny_night_runner_v5';

const storageKeyFor = (childId) =>
  childId ? `${STORAGE_KEY_BASE}:${childId}` : STORAGE_KEY_BASE;

const CANVAS_W = 1280;
const CANVAS_H = 720;
const PLAYER_W = 190;
const PLAYER_H = 170;
const PLAYER_MIN_X = 95;
const PLAYER_MAX_X = 425;
const PLAYER_START_X = 175;
const GRAVITY = 2450;
const JUMP_VELOCITY = -930;
const MOVE_SPEED = 340;

const ASSETS = {
  bg1: '/assets/runner/v8/bg-level-1-rooftops.webp',
  bg2: '/assets/runner/v8/bg-level-2-moon-bridge.webp',
  bg3: '/assets/runner/v8/bg-level-3-star-gate.webp',
  nimnyRunA: '/assets/runner/v8/nimny-run-frame-a.webp',
  nimnyRunB: '/assets/runner/v8/nimny-run-frame-b.webp',
  nimnyRunC: '/assets/runner/v8/nimny-run-frame-c.webp',
  nimnyRunD: '/assets/runner/v8/nimny-run-frame-d.webp',
  nimnyRunE: '/assets/runner/v8/nimny-run-frame-e.webp',
  nimnyRunF: '/assets/runner/v8/nimny-run-frame-f.webp',
  nimnyJump: '/assets/runner/v8/nimny-jump-frame.webp',
  star: '/assets/runner/v8/star.webp',
  crate: '/assets/runner/v8/crate.webp',
  branch: '/assets/runner/v8/branch.webp',
  cloud: '/assets/runner/v8/sleepy-cloud.webp',
  portal: '/assets/runner/v8/portal.webp'
};

const RUN_FRAME_KEYS = ['nimnyRunA', 'nimnyRunB', 'nimnyRunC', 'nimnyRunD'];

const LEVELS = [
  {
    id: 1,
    bg: 'bg1',
    surfaceY: 510,
    target: 20,
    title: { ru: 'Ночные крыши', en: 'Night Rooftops' },
    objective: { ru: 'Собери 20 звёзд за одну пробежку', en: 'Collect 20 stars in one run' },
    blurb: { ru: 'Разогрей фонарик Нимни и собери первые огоньки.', en: 'Light Nimny’s lantern and gather the first stars.' },
    speedStart: 245,
    speedMax: 440,
    obstacleMin: 1.75,
    obstacleMax: 2.65,
    platformRate: 0.12,
    tint: 'rgba(7, 16, 55, 0.05)',
    accent: '#f6c64a'
  },
  {
    id: 2,
    bg: 'bg2',
    surfaceY: 421,
    target: 30,
    title: { ru: 'Лунный мост', en: 'Moon Bridge' },
    objective: { ru: 'Собери 30 звёзд и запрыгивай на полочки', en: 'Collect 30 stars and jump onto ledges' },
    blurb: { ru: 'Теперь путь идёт по сияющему лунному мосту.', en: 'Now the path crosses a glowing moon bridge.' },
    speedStart: 275,
    speedMax: 490,
    obstacleMin: 1.45,
    obstacleMax: 2.25,
    platformRate: 0.52,
    tint: 'rgba(20, 46, 130, 0.04)',
    accent: '#cdd9ff'
  },
  {
    id: 3,
    bg: 'bg3',
    surfaceY: 560,
    target: 40,
    title: { ru: 'Звёздные ворота', en: 'Star Gate' },
    objective: { ru: 'Собери 40 звёзд и доберись до ворот', en: 'Collect 40 stars and reach the gate' },
    blurb: { ru: 'Финальная пробежка ведёт к сияющим звёздным воротам.', en: 'The final run leads to the glowing star gate.' },
    speedStart: 300,
    speedMax: 525,
    obstacleMin: 1.25,
    obstacleMax: 2.0,
    platformRate: 0.70,
    tint: 'rgba(68, 30, 105, 0.05)',
    accent: '#ffe1b8'
  }
];

const OBSTACLE_TYPES = [
  { key: 'crate', asset: 'crate', w: 76, h: 76, hitPadX: 14, hitPadY: 10 },
  { key: 'branch', asset: 'branch', w: 116, h: 48, hitPadX: 18, hitPadY: 14 },
  { key: 'cloud', asset: 'cloud', w: 92, h: 70, hitPadX: 16, hitPadY: 12 }
];

const loadStoredProgress = (childId) => {
  if (typeof window === 'undefined') return { unlocked: 1, best: {}, allDone: false };
  try {
    let raw = window.localStorage.getItem(storageKeyFor(childId));
    // Back-compat: existing players had a global key. On first read for the
    // Max child, fall back to the legacy global key so best scores survive.
    if (!raw && childId === 'max') {
      raw = window.localStorage.getItem(STORAGE_KEY_BASE);
    }
    if (!raw) return { unlocked: 1, best: {}, allDone: false };
    const parsed = JSON.parse(raw);
    return {
      unlocked: Math.min(3, Math.max(1, Number(parsed.unlocked) || 1)),
      best: parsed.best && typeof parsed.best === 'object' ? parsed.best : {},
      allDone: !!parsed.allDone
    };
  } catch (_e) {
    return { unlocked: 1, best: {}, allDone: false };
  }
};

const saveStoredProgress = (childId, next) => {
  try { window.localStorage.setItem(storageKeyFor(childId), JSON.stringify(next)); } catch (_e) { /* ignore */ }
};

const txFor = (language) => (ru, en) => (language === 'ru' ? ru : en);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const rand = (min, max) => min + Math.random() * (max - min);
const rectsOverlap = (a, b) => (
  a.x < b.x + b.w &&
  a.x + a.w > b.x &&
  a.y < b.y + b.h &&
  a.y + a.h > b.y
);

// ============================================================================
// Top-level page — preserves existing lock behavior.
// ============================================================================
const NimnyNightRunner = () => {
  const { t, user, childProgress } = useLab();
  const params = useParams();
  const location = useLocation();
  const childId = resolveLabChildId({
    routeStudentId: params.studentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(childId);
  const progress = childProgress(childId);
  const courseTemplate = getCourseTemplateForChild(user, childId);
  const weeks = getWeeksForCourse(courseTemplate);
  const day5Status = computeDayStatus(progress, 'week-10', 'day-5', weeks);
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  const unlocked = isParent || day5Status === 'completed';

  if (!unlocked) {
    return (
      <LabLayout>
        <BackLink t={t} base={base} />
        <Card className="mt-6 p-8 text-center" data-testid="night-runner-locked">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-300/10 border border-amber-300/25 flex items-center justify-center text-amber-200/80">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="text-2xl text-[#f3d9a4]" style={{ fontFamily: 'var(--font-heading)' }} data-testid="night-runner-locked-title">
            {t('nightRunnerLockedTitle')}
          </h1>
          <p className="mt-2 text-amber-200/70 font-serif text-sm">
            {t('nightRunnerLockedHint')}
          </p>
          <Link
            to={`${base}/week-10/quest`}
            className="inline-block mt-5 px-5 py-3 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] font-sans"
            data-testid="night-runner-back-quest"
          >
            {t('questOpenMap')}
          </Link>
        </Card>
      </LabLayout>
    );
  }

  return <RunnerApp t={t} base={base} childId={childId} />;
};

const BackLink = ({ t, base }) => (
  <Link
    to={`${base}/week-10/quest`}
    className="inline-flex items-center gap-1.5 text-amber-200/75 hover:text-amber-200 text-sm font-sans"
    data-testid="night-runner-back"
  >
    <ChevronLeft className="w-4 h-4" />
    {t('back')}
  </Link>
);

// ============================================================================
// App shell — level select, canvas game, result screens.
// ============================================================================
const RunnerApp = ({ t, base, childId }) => {
  const { language } = useLab();
  const tx = useMemo(() => txFor(language), [language]);
  const [progress, setProgress] = useState(() => loadStoredProgress(childId));
  const [view, setView] = useState('select'); // select | playing | won | lost | final
  const [levelId, setLevelId] = useState(1);
  const [lastScore, setLastScore] = useState(0);
  const level = LEVELS.find((lv) => lv.id === levelId) || LEVELS[0];

  const persistProgress = useCallback((updater) => {
    setProgress((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveStoredProgress(childId, next);
      return next;
    });
  }, [childId]);

  const startLevel = (id) => {
    if (id > progress.unlocked) return;
    setLevelId(id);
    setLastScore(0);
    setView('playing');
  };

  const handleWin = (score) => {
    setLastScore(score);
    persistProgress((prev) => ({
      unlocked: Math.max(prev.unlocked, Math.min(3, levelId + 1)),
      best: { ...prev.best, [levelId]: Math.max(Number(prev.best?.[levelId]) || 0, score) },
      allDone: prev.allDone || levelId === 3
    }));
    setView(levelId === 3 ? 'final' : 'won');
  };

  const handleLoss = (score) => {
    setLastScore(score);
    persistProgress((prev) => ({
      ...prev,
      best: { ...prev.best, [levelId]: Math.max(Number(prev.best?.[levelId]) || 0, score) }
    }));
    setView('lost');
  };

  return (
    <LabLayout>
      <div className="night-runner-wide-shell">
        <BackLink t={t} base={base} />
        <header className="night-runner-page-head" data-testid="night-runner-header">
          <div>
            <p className="night-runner-kicker">{tx('Финальная игра курса', 'Final course game')}</p>
            <h1 className="night-runner-page-title" data-testid="night-runner-title">{t('nightRunnerTitle')}</h1>
            <p className="night-runner-page-subtitle">
              {tx('Помоги Нимни собрать огоньки ночи и открыть звёздные ворота.', 'Help Nimny gather the night lights and open the star gate.')}
            </p>
          </div>
          <ParentPreviewBadge />
        </header>

        {view === 'select' && (
          <LevelSelect tx={tx} progress={progress} onPick={startLevel} />
        )}

        {view === 'playing' && (
          <CanvasRunner
            tx={tx}
            level={level}
            onWin={handleWin}
            onLoss={handleLoss}
          />
        )}

        {view === 'won' && (
          <ResultPanel
            tx={tx}
            kind="won"
            level={level}
            score={lastScore}
            onRetry={() => startLevel(level.id)}
            onNext={() => startLevel(level.id + 1)}
            onSelect={() => setView('select')}
          />
        )}

        {view === 'lost' && (
          <ResultPanel
            tx={tx}
            kind="lost"
            level={level}
            score={lastScore}
            onRetry={() => startLevel(level.id)}
            onSelect={() => setView('select')}
          />
        )}

        {view === 'final' && (
          <ResultPanel
            tx={tx}
            kind="final"
            level={level}
            score={lastScore}
            onRetry={() => startLevel(3)}
            onSelect={() => setView('select')}
          />
        )}
      </div>
    </LabLayout>
  );
};

const LevelSelect = ({ tx, progress, onPick }) => (
  <section className="night-runner-select" data-testid="night-runner-level-select">
    <div className="night-runner-select-intro">
      <h2>{tx('Выбери пробежку', 'Choose a run')}</h2>
      <p>{tx('Каждый уровень открывает новую часть ночного пути Нимни.', 'Each level opens a new part of Nimny’s night path.')}</p>
    </div>
    <div className="night-runner-level-grid">
      {LEVELS.map((level) => {
        const locked = level.id > progress.unlocked;
        const best = Number(progress.best?.[level.id]) || 0;
        return (
          <button
            type="button"
            key={level.id}
            className={`night-runner-level-card ${locked ? 'is-locked' : 'is-open'}`}
            onClick={() => !locked && onPick(level.id)}
            disabled={locked}
            data-testid={`night-runner-level-${level.id}`}
            data-locked={locked ? 'true' : 'false'}
          >
            <span className="night-runner-level-top">
              <span>{tx('Уровень', 'Level')} {level.id}</span>
              {locked ? <Lock className="w-4 h-4" /> : <Star className="w-4 h-4" />}
            </span>
            <strong>{tx(level.title.ru, level.title.en)}</strong>
            <em>{tx(level.blurb.ru, level.blurb.en)}</em>
            <span className="night-runner-level-meta">
              <span>⭐ {level.target}</span>
              <span>{tx('Лучший', 'Best')}: {best}</span>
            </span>
          </button>
        );
      })}
    </div>
    {progress.allDone && (
      <div className="night-runner-final-hint" data-testid="night-runner-all-done-hint">
        <Trophy className="w-4 h-4" />
        {tx('Нимни собрала огоньки ночи.', 'Nimny has gathered the lights of the night.')}
      </div>
    )}
  </section>
);

const ResultPanel = ({ tx, kind, level, score, onRetry, onNext, onSelect }) => {
  const title = {
    won: tx('Уровень пройден!', 'Level complete!'),
    lost: tx('Тихая остановка', 'Quiet stop'),
    final: tx('Нимни собрала огоньки ночи.', 'Nimny gathered the lights of the night.')
  }[kind];

  const subtitle = {
    won: tx('Звёздные ворота засияли. Следующая пробежка открыта.', 'The star gate glows. The next run is open.'),
    lost: tx('Нимни можно отдохнуть и попробовать ещё раз.', 'Nimny can rest and try again.'),
    final: tx('Финальный свет курса открыт. Это твой приз за путь Нимни.', 'The final course light is open. This is your reward for the Nimny path.')
  }[kind];

  return (
    <section className={`night-runner-result is-${kind}`} data-testid={`night-runner-result-${kind}`}>
      <div className="night-runner-result-card">
        {kind === 'final' ? <Trophy className="night-runner-result-icon" /> : <Star className="night-runner-result-icon" />}
        <p className="night-runner-result-level">{tx(level.title.ru, level.title.en)} · {tx('Уровень', 'Level')} {level.id}</p>
        <h2>{title}</h2>
        <p>{subtitle}</p>
        <div className="night-runner-result-score">⭐ {score} / {level.target}</div>
        <div className="night-runner-result-actions">
          {kind === 'won' && onNext && (
            <button type="button" className="night-runner-primary-btn" onClick={onNext} data-testid="night-runner-next-level">
              <Play className="w-4 h-4" /> {tx('Следующий уровень', 'Next level')}
            </button>
          )}
          <button type="button" className="night-runner-secondary-btn" onClick={onRetry} data-testid="night-runner-play-again">
            <Play className="w-4 h-4" /> {kind === 'lost' ? tx('Попробуй ещё раз', 'Try again') : tx('Сыграть ещё раз', 'Play again')}
          </button>
          <button type="button" className="night-runner-secondary-btn" onClick={onSelect} data-testid="night-runner-back-select">
            {tx('Выбор уровня', 'Levels')}
          </button>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Canvas game.
// ============================================================================
const CanvasRunner = ({ tx, level, onWin, onLoss }) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const assetsRef = useRef({});
  const keysRef = useRef({ left: false, right: false });
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const gameRef = useRef(null);
  const [assetsReady, setAssetsReady] = useState(false);
  const [paused, setPaused] = useState(false);

  const loadAssets = useCallback(() => {
    setAssetsReady(false);
    const entries = Object.entries(ASSETS);
    const loaded = {};
    let done = 0;
    entries.forEach(([key, src]) => {
      const image = new Image();
      image.onload = () => {
        loaded[key] = image;
        done += 1;
        if (done === entries.length) {
          assetsRef.current = loaded;
          setAssetsReady(true);
        }
      };
      image.onerror = () => {
        done += 1;
        if (done === entries.length) {
          assetsRef.current = loaded;
          setAssetsReady(true);
        }
      };
      image.src = src;
    });
  }, []);

  const resetGame = useCallback(() => {
    gameRef.current = createInitialGame(level);
    lastRef.current = 0;
    setPaused(false);
  }, [level]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  useEffect(() => {
    resetGame();
  }, [resetGame]);

  const jump = useCallback(() => {
    const g = gameRef.current;
    if (!g || paused || g.done) return;
    if (g.player.onGround) {
      g.player.vy = JUMP_VELOCITY;
      g.player.onGround = false;
    }
  }, [paused]);

  useEffect(() => {
    const down = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keysRef.current.left = true;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keysRef.current.right = true;
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        e.preventDefault();
        jump();
      }
      if (e.code === 'KeyP') setPaused((p) => !p);
    };
    const up = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keysRef.current.left = false;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keysRef.current.right = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [jump]);

  useEffect(() => {
    if (!assetsReady) return undefined;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const tick = (ts) => {
      const last = lastRef.current || ts;
      lastRef.current = ts;
      const dt = Math.min(0.04, (ts - last) / 1000);
      const g = gameRef.current;
      if (!g) return;

      if (!paused && !g.done) updateGame(g, level, keysRef.current, dt);
      drawGame(ctx, g, level, assetsRef.current, tx, paused);

      if (g.done === 'lost') {
        window.cancelAnimationFrame(rafRef.current);
        onLoss(g.score);
        return;
      }
      if (g.done === 'won') {
        window.cancelAnimationFrame(rafRef.current);
        onWin(g.score);
        return;
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, [assetsReady, paused, level, onLoss, onWin, tx]);

  return (
    <section className="night-runner-canvas-shell" data-testid="night-runner-stage">
      <div className="night-runner-canvas-frame" ref={wrapRef}>
        {!assetsReady && (
          <div className="night-runner-loading">
            {tx('Нимни зажигает фонарик…', 'Nimny is lighting the lantern…')}
          </div>
        )}
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="night-runner-canvas"
          onClick={jump}
          onTouchStart={(e) => { e.preventDefault(); jump(); }}
          aria-label="Nimny Night Runner"
        />
        <button
          type="button"
          className="night-runner-pause-btn"
          onClick={(e) => { e.stopPropagation(); setPaused((p) => !p); }}
          data-testid="night-runner-pause"
          aria-label="pause"
        >
          {paused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
        </button>
        <div className="night-runner-controls" aria-label="runner controls">
          <button
            type="button"
            onPointerDown={() => { keysRef.current.left = true; }}
            onPointerUp={() => { keysRef.current.left = false; }}
            onPointerLeave={() => { keysRef.current.left = false; }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button type="button" onClick={jump} data-testid="night-runner-jump" className="is-jump">
            <ArrowUp className="w-6 h-6" />
          </button>
          <button
            type="button"
            onPointerDown={() => { keysRef.current.right = true; }}
            onPointerUp={() => { keysRef.current.right = false; }}
            onPointerLeave={() => { keysRef.current.right = false; }}
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      <p className="night-runner-help">
        {tx('Клавиши: ← → двигаться, Space или ↑ прыгать, P пауза.', 'Keys: ← → move, Space or ↑ jump, P pause.')}
      </p>
    </section>
  );
};

const createInitialGame = (level) => ({
  player: {
    x: PLAYER_START_X,
    y: level.surfaceY - PLAYER_H,
    vy: 0,
    onGround: true
  },
  speed: level.speedStart,
  distance: 0,
  score: 0,
  streak: 0,
  streakTimer: 0,
  gateMessage: 0,
  stars: [],
  obstacles: [],
  platforms: [],
  particles: [],
  portal: null,
  obstacleTimer: 1.8,
  starTimer: 0.4,
  platformTimer: level.id === 1 ? 2.7 : 1.55,
  done: null
});

const updateGame = (g, level, keys, dt) => {
  const player = g.player;

  if (keys.left) player.x -= MOVE_SPEED * dt;
  if (keys.right) player.x += MOVE_SPEED * dt;
  player.x = clamp(player.x, PLAYER_MIN_X, PLAYER_MAX_X);

  const prevBottom = player.y + PLAYER_H;
  player.vy += GRAVITY * dt;
  player.y += player.vy * dt;
  player.onGround = false;

  // ground landing
  if (player.y + PLAYER_H >= level.surfaceY) {
    player.y = level.surfaceY - PLAYER_H;
    player.vy = 0;
    player.onGround = true;
  }

  // platform landing
  for (const p of g.platforms) {
    const platformTop = p.y;
    const playerBottom = player.y + PLAYER_H;
    const overlapsX = player.x + PLAYER_W * 0.75 > p.x && player.x + PLAYER_W * 0.25 < p.x + p.w;
    if (player.vy >= 0 && prevBottom <= platformTop + 8 && playerBottom >= platformTop && overlapsX) {
      player.y = platformTop - PLAYER_H;
      player.vy = 0;
      player.onGround = true;
    }
  }

  g.distance += g.speed * dt;
  g.speed = Math.min(level.speedMax, level.speedStart + g.distance / 70);

  const dx = g.speed * dt;
  g.stars.forEach((s) => { s.x -= dx; s.pulse += dt; });
  g.obstacles.forEach((o) => { o.x -= dx; });
  g.platforms.forEach((p) => { p.x -= dx; });
  if (g.portal) g.portal.x -= dx;
  g.particles.forEach((p) => { p.x += p.vx * dt; p.y += p.vy * dt; p.life -= dt; });

  g.stars = g.stars.filter((s) => !s.taken && s.x > -80);
  g.obstacles = g.obstacles.filter((o) => o.x + o.w > -100);
  g.platforms = g.platforms.filter((p) => p.x + p.w > -100);
  g.particles = g.particles.filter((p) => p.life > 0);

  spawnWorld(g, level, dt);
  collectStars(g, level);
  checkObstacleHit(g);
  checkPortal(g, level);

  if (g.streakTimer > 0) g.streakTimer -= dt;
  if (g.gateMessage > 0) g.gateMessage -= dt;
};

const spawnWorld = (g, level, dt) => {
  if (!g.portal) {
    g.obstacleTimer -= dt;
    if (g.obstacleTimer <= 0) {
      spawnObstacle(g, level);
      const k = (g.speed - level.speedStart) / Math.max(1, (level.speedMax - level.speedStart));
      const min = level.obstacleMin - 0.25 * k;
      const max = level.obstacleMax - 0.35 * k;
      g.obstacleTimer = rand(min, Math.max(min + 0.35, max));
    }

    g.platformTimer -= dt;
    if (g.platformTimer <= 0) {
      if (Math.random() < level.platformRate) spawnPlatform(g, level);
      g.platformTimer = level.id === 1 ? rand(2.8, 4.2) : rand(1.7, 2.9);
    }

    g.starTimer -= dt;
    if (g.starTimer <= 0) {
      spawnStarArc(g, level);
      g.starTimer = rand(0.85, 1.45);
    }
  }
};

const spawnObstacle = (g, level) => {
  const type = OBSTACLE_TYPES[Math.floor(Math.random() * OBSTACLE_TYPES.length)];
  const baseY = level.surfaceY;
  g.obstacles.push({
    ...type,
    x: CANVAS_W + rand(70, 220),
    y: baseY - type.h
  });
};

const spawnPlatform = (g, level) => {
  const surface = level.surfaceY;
  const yChoices = level.id === 2
    ? [surface - 150, surface - 110]
    : level.id === 3
      ? [surface - 225, surface - 170, surface - 120]
      : [surface - 135];

  const y = yChoices[Math.floor(Math.random() * yChoices.length)];
  const w = level.id === 3 ? rand(190, 275) : rand(210, 310);
  const x = CANVAS_W + rand(90, 220);
  const platform = { x, y, w, h: 24 };
  g.platforms.push(platform);

  const count = level.id === 3 ? 5 : 4;
  for (let i = 0; i < count; i += 1) {
    g.stars.push({
      x: x + 30 + i * 42,
      y: y - 62 - Math.sin(i / 1.3) * 15,
      r: 21,
      pulse: Math.random() * 2,
      taken: false
    });
  }
};

const spawnStarArc = (g, level) => {
  const group = 4 + Math.floor(Math.random() * (level.id + 2));
  const high = Math.random() < (level.id === 1 ? 0.25 : 0.55);
  const baseY = high
    ? clamp(rand(level.surfaceY - 300, level.surfaceY - 190), 95, level.surfaceY - 70)
    : clamp(rand(level.surfaceY - 155, level.surfaceY - 80), 95, level.surfaceY - 60);
  const x0 = CANVAS_W + rand(60, 160);
  for (let i = 0; i < group; i += 1) {
    g.stars.push({
      x: x0 + i * 48,
      y: baseY - Math.sin(i / Math.max(1, group - 1) * Math.PI) * rand(35, 70),
      r: 21,
      pulse: Math.random() * 2,
      taken: false
    });
  }
};

const collectStars = (g) => {
  const px = g.player.x + PLAYER_W * 0.5;
  const py = g.player.y + PLAYER_H * 0.48;
  for (const s of g.stars) {
    const dx = s.x - px;
    const dy = s.y - py;
    if (dx * dx + dy * dy < 46 * 46) {
      s.taken = true;
      g.score += 1;
      g.streak += 1;
      g.streakTimer = 1.25;
      addSparkles(g, s.x, s.y, 8);
      if (g.score >= 10 && g.score % 10 === 0) addSparkles(g, g.player.x + PLAYER_W, g.player.y + 60, 16);
      if (g.score >= 0) maybeOpenPortal(g);
    }
  }
};

const maybeOpenPortal = (g) => {
  if (g.portal) return;
  // Actual target check is in checkPortal/update. This function is only called when score changes.
};

const checkObstacleHit = (g) => {
  const playerBox = {
    x: g.player.x + 50,
    y: g.player.y + 36,
    w: PLAYER_W - 96,
    h: PLAYER_H - 70
  };
  for (const o of g.obstacles) {
    const box = {
      x: o.x + o.hitPadX + 4,
      y: o.y + o.hitPadY + 4,
      w: Math.max(8, o.w - (o.hitPadX + 4) * 2),
      h: Math.max(8, o.h - (o.hitPadY + 4) * 2)
    };
    if (rectsOverlap(playerBox, box)) {
      g.done = 'lost';
      return;
    }
  }
};

const checkPortal = (g, level) => {
  if (!g.portal && g.score >= level.target) {
    g.portal = { x: CANVAS_W + 360, y: Math.max(60, level.surfaceY - 280), w: 210, h: 270 };
    g.gateMessage = 3.2;
  }
  if (!g.portal) return;
  const playerBox = { x: g.player.x + 30, y: g.player.y + 24, w: PLAYER_W - 56, h: PLAYER_H - 40 };
  const portalBox = { x: g.portal.x + 45, y: g.portal.y + 38, w: g.portal.w - 80, h: g.portal.h - 55 };
  if (rectsOverlap(playerBox, portalBox)) g.done = 'won';
};

const addSparkles = (g, x, y, count) => {
  for (let i = 0; i < count; i += 1) {
    g.particles.push({
      x,
      y,
      vx: rand(-90, 90),
      vy: rand(-140, -20),
      life: rand(0.35, 0.85),
      size: rand(2, 6)
    });
  }
};

const drawGame = (ctx, g, level, img, tx, paused) => {
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  drawBackground(ctx, img[level.bg] || img.bg1, g.distance, level);
  drawPlatforms(ctx, g.platforms);
  drawStars(ctx, g.stars, img.star);
  drawObstacles(ctx, g.obstacles, img);
  if (g.portal) drawPortal(ctx, g.portal, img.portal);
  drawParticles(ctx, g.particles);
  drawPlayer(ctx, g.player, img, g.distance);
  drawHud(ctx, g, level, tx);
  if (g.streakTimer > 0 && g.streak >= 5) drawCenterToast(ctx, tx('Огонёк!', 'Light streak!'));
  if (g.gateMessage > 0) drawCenterToast(ctx, tx('Звёздные ворота открылись!', 'The star gate is open!'), 130);
  if (paused) drawPauseOverlay(ctx, tx);
};

const drawBackground = (ctx, bg, distance, level) => {
  if (!bg) {
    ctx.fillStyle = '#071037';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    return;
  }
  const scale = Math.max(CANVAS_W / bg.width, CANVAS_H / bg.height);
  const w = bg.width * scale;
  const h = bg.height * scale;
  const y = (CANVAS_H - h) / 2;
  const shift = -((distance * 0.12) % w);
  ctx.drawImage(bg, shift, y, w, h);
  ctx.drawImage(bg, shift + w, y, w, h);
  if (shift > 0) ctx.drawImage(bg, shift - w, y, w, h);
  ctx.fillStyle = level.tint;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
};

const drawPlatforms = (ctx, platforms) => {
  for (const p of platforms) {
    const grd = ctx.createLinearGradient(0, p.y, 0, p.y + p.h);
    grd.addColorStop(0, '#d2a15e');
    grd.addColorStop(1, '#6b3f22');
    roundRect(ctx, p.x, p.y, p.w, p.h, 12, grd, 'rgba(24, 12, 5, 0.55)', 2);
    ctx.fillStyle = 'rgba(255, 224, 140, 0.22)';
    ctx.fillRect(p.x + 16, p.y + 5, p.w - 32, 2);
  }
};

const drawStars = (ctx, stars, starImg) => {
  for (const s of stars) {
    const scale = 1 + Math.sin(s.pulse * 4) * 0.07;
    const size = 44 * scale;
    ctx.save();
    ctx.globalAlpha = 0.92;
    ctx.shadowColor = '#f6c64a';
    ctx.shadowBlur = 18;
    if (starImg) ctx.drawImage(starImg, s.x - size / 2, s.y - size / 2, size, size);
    else {
      ctx.fillStyle = '#f6c64a';
      ctx.beginPath(); ctx.arc(s.x, s.y, 18, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }
};

const drawObstacles = (ctx, obstacles, img) => {
  for (const o of obstacles) {
    const image = img[o.asset];
    if (image) ctx.drawImage(image, o.x, o.y, o.w, o.h);
    else roundRect(ctx, o.x, o.y, o.w, o.h, 12, '#8b5a2b', '#3e2811', 2);
  }
};

const drawPortal = (ctx, portal, portalImg) => {
  ctx.save();
  ctx.shadowColor = '#f6c64a';
  ctx.shadowBlur = 30;
  if (portalImg) ctx.drawImage(portalImg, portal.x, portal.y, portal.w, portal.h);
  else roundRect(ctx, portal.x, portal.y, portal.w, portal.h, 24, '#f6c64a', '#fff2b2', 3);
  ctx.restore();
};

const drawPlayer = (ctx, player, img, distance) => {
  const jumping = !player.onGround;
  const frameIndex = Math.floor(distance / 30) % RUN_FRAME_KEYS.length;
  const image = jumping ? img.nimnyJump : img[RUN_FRAME_KEYS[frameIndex]];
  const bob = jumping ? 0 : Math.sin(distance / 18) * 2;
  const x = player.x;
  const y = player.y + bob;

  ctx.save();
  ctx.shadowColor = 'rgba(246, 198, 74, 0.8)';
  ctx.shadowBlur = 18;
  if (image) ctx.drawImage(image, x, y, PLAYER_W, PLAYER_H);
  else roundRect(ctx, x, y, PLAYER_W, PLAYER_H, 18, '#f3d9a4', '#d4a574', 2);
  ctx.restore();
};

const drawParticles = (ctx, particles) => {
  for (const p of particles) {
    ctx.save();
    ctx.globalAlpha = clamp(p.life, 0, 1);
    ctx.fillStyle = '#fff2b2';
    ctx.shadowColor = '#f6c64a';
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
};

const drawHud = (ctx, g, level, tx) => {
  ctx.save();
  ctx.font = '700 30px Nunito, sans-serif';
  drawGlassPill(ctx, 28, 24, 210, 64);
  ctx.fillStyle = '#fff2b2';
  ctx.fillText(`⭐ ${g.score} / ${level.target}`, 52, 66);

  drawGlassPill(ctx, CANVAS_W / 2 - 220, 24, 440, 62);
  ctx.fillStyle = '#f3d9a4';
  ctx.font = '700 22px Nunito, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(`${tx('Уровень', 'Level')} ${level.id} · ${tx(level.title.ru, level.title.en)}`, CANVAS_W / 2, 50);
  const progress = clamp(g.score / level.target, 0, 1);
  roundRect(ctx, CANVAS_W / 2 - 170, 62, 340, 12, 999, 'rgba(255,255,255,0.18)', 'rgba(255,255,255,0.22)', 1);
  roundRect(ctx, CANVAS_W / 2 - 170, 62, 340 * progress, 12, 999, '#f6c64a', 'rgba(255,255,255,0.25)', 1);

  ctx.textAlign = 'left';
  drawGlassPill(ctx, 28, CANVAS_H - 82, 470, 54);
  ctx.font = '700 20px Nunito, sans-serif';
  ctx.fillStyle = '#fff8e1';
  ctx.fillText(`⭐ ${tx(level.objective.ru, level.objective.en)}`, 52, CANVAS_H - 48);
  ctx.restore();
};

const drawCenterToast = (ctx, text, y = 102) => {
  ctx.save();
  ctx.font = '800 34px Nunito, sans-serif';
  const metrics = ctx.measureText(text);
  const w = metrics.width + 70;
  const x = CANVAS_W / 2 - w / 2;
  drawGlassPill(ctx, x, y, w, 68, true);
  ctx.textAlign = 'center';
  ctx.fillStyle = '#fff8e1';
  ctx.shadowColor = '#f6c64a';
  ctx.shadowBlur = 12;
  ctx.fillText(text, CANVAS_W / 2, y + 44);
  ctx.restore();
};

const drawPauseOverlay = (ctx, tx) => {
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.font = '800 52px Nunito, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#fff8e1';
  ctx.fillText(tx('Пауза', 'Paused'), CANVAS_W / 2, CANVAS_H / 2);
  ctx.restore();
};

const drawGlassPill = (ctx, x, y, w, h, strong = false) => {
  roundRect(
    ctx,
    x,
    y,
    w,
    h,
    h / 2,
    strong ? 'rgba(12, 16, 40, 0.70)' : 'rgba(8, 12, 30, 0.45)',
    'rgba(246, 198, 74, 0.38)',
    2
  );
};

const roundRect = (ctx, x, y, w, h, r, fill, stroke, line = 1) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = line;
    ctx.stroke();
  }
};

export default NimnyNightRunner;
